import React, { useEffect, useState } from "react";
import Info from "common-ui-components/components/Info";
import DataPanel from "common-ui-components/components/data-panel/DataPanel";
import Patch_API from "common-ui-components/components/utils/Patch_API";

const PatchOverview = ({ patch }) => {
    const [patchDetails, setPatchDetails] = useState({});
    const [isLoading, setIsLoading] = useState(true);

    const wrapText = (text) => {
        if (!text) return "NA";

        return (
            <div
                style={{
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    overflow: "visible",
                    textOverflow: "unset"
                }}
            >
                {text}
            </div>
        );
    };

    const shimmerLine = (width = "100%") => (
        <span
            className="shimmerAnimation"
            style={{ width, height: "16px", borderRadius: "4px" }}
        />
    );

    const sectionHeader = (text) =>
        isLoading ? shimmerLine("190px") : text;

    useEffect(() => {

        const fetchPatchDetails = async () => {
            setIsLoading(true);
            try {

                const response = await Patch_API.post("/details", {
                    patchId: patch?.masterPatchId,
                    masterPatchId: patch?.masterPatchId,
                    unknown: patch?.isUnknownPatch
                });

                setPatchDetails(response?.body || {});

            } catch (error) {
                //console.log(error);
                setPatchDetails({});
            } finally {
                setIsLoading(false);
            }
        };

        if (patch?.masterPatchId !== undefined) {
            fetchPatchDetails();
        } else {
            setPatchDetails({});
            setIsLoading(false);
        }

    }, [patch?.patchId, patch?.isUnknownPatch]);

    const basicInformation = isLoading
        ? {
            Patch_Id: shimmerLine("120px"),
            Kb_Id: shimmerLine("140px"),
            Patch_Name: shimmerLine("90%"),
            Patch_Description: shimmerLine("95%"),
            Patch_Type: shimmerLine("130px"),
            Platform: shimmerLine("110px"),
            Vendor: shimmerLine("120px"),
            Architecture: shimmerLine("140px"),
            File_Size: shimmerLine("100px"),
        }
        : {
            Patch_Id: patchDetails.masterPatchId || "NA",
            Kb_Id: patchDetails.kbId || "NA",
            Patch_Name: wrapText(patchDetails.title),
            Patch_Description: wrapText(patchDetails.description),
            Patch_Type: patchDetails.patchType || "NA",
            Platform: patchDetails.platform || "NA",
            Vendor: patchDetails.vendor || "NA",
            Architecture: patchDetails.architecture || "NA",
            File_Size: patchDetails.fileSize || "NA",
        };

    const securityInformation = isLoading
        ? {
            CVE_Ids: shimmerLine("90%"),
            Affected_Products: shimmerLine("92%"),
            Severity: shimmerLine("120px"),
            CVSS_Score: shimmerLine("100px"),
        }
        : {
            CVE_Ids: wrapText(
                patchDetails.cves?.length
                    ? patchDetails.cves.join(", ")
                    : "NA"
            ),
            Affected_Products: wrapText(patchDetails?.affectedProducts),
            Severity: wrapText(patchDetails?.severity),
            CVSS_Score: patchDetails?.cvssScore || "NA",
        };

    const releaseSupportInformation = isLoading
        ? {
            Bulletin_Id: shimmerLine("130px"),
            Superseding_Patch_Id: shimmerLine("85%"),
            Superseded_By: shimmerLine("85%"),
        }
        : {
            Bulletin_Id: patchDetails.bulletinId || "NA",
            Superseding_Patch_Id: wrapText(patchDetails.supersedes),
            Superseded_By: wrapText(patchDetails.supersededBy),
        };

    const installationInformation = isLoading
        ? {
            Reboot_Required: shimmerLine("110px"),
            Network_Connectivity_Required: shimmerLine("180px"),
            More_Info_Url: shimmerLine("95%"),
            Support_Url: shimmerLine("95%"),
            Download_Url: shimmerLine("95%"),
        }
        : {
            Reboot_Required: patchDetails.rebootRequired || "NA",
            Network_Connectivity_Required: patchDetails.networkConnectivityRequired || "NA",
            More_Info_Url: patchDetails?.moreInfoUrl ? (
                <a
                    href={patchDetails.moreInfoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                        color: "#3b82f6",
                        textDecoration: "underline",
                        wordBreak: "break-all"
                    }}
                >
                    {patchDetails.moreInfoUrl}
                </a>
            ) : "NA",
            Support_Url: patchDetails?.supportUrl ? (
                <a
                    href={patchDetails.supportUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                        color: "#3b82f6",
                        textDecoration: "underline",
                        wordBreak: "break-all"
                    }}
                >
                    {patchDetails.supportUrl}
                </a>
            ) : "NA",
            Download_Url: patchDetails.downloadUrl?.[0] ? (
                <a
                    href={patchDetails.downloadUrl[0]}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                        color: "#3b82f6",
                        textDecoration: "underline",
                        wordBreak: "break-all"
                    }}
                >
                    {patchDetails.downloadUrl[0]}
                </a>
            ) : "NA",
        };

    return (
        <section
            style={{
                padding: "0.8rem"
            }}
        >

            {isLoading ? (
                <div style={{ width: "170px" }}>
                    {shimmerLine("100%")}
                </div>
            ) : (
                <p>Patch Details</p>
            )}

            <div
                style={{
                    height: "1px",
                    background: "#e5e7eb",
                    marginBottom: "1rem"
                }}
            />

            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "1rem",
                }}
            >

                <Info header={sectionHeader("Basic Information")}>
                    <DataPanel data={basicInformation} />
                </Info>

                <Info header={sectionHeader("Security Information")}>
                    <DataPanel data={securityInformation} />
                </Info>

                <Info header={sectionHeader("Release & Support Information")}>
                    <DataPanel data={releaseSupportInformation} />
                </Info>

                <Info header={sectionHeader("Installation Information")}>
                    <DataPanel data={installationInformation} />
                </Info>

            </div>

        </section>
    );
};

export default PatchOverview;
